import { useState } from 'react';

import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Col,
  Combobox,
  type HookFormMethods,
  HookFormResolver,
  PageTitle,
  Row
} from '@dec/shared';
import { type ColDef } from 'ag-grid-community';
import { notificationSuccess } from 'app/core/toast';
import { buttons as buttonCommon } from 'app/modules/translation';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ActionsGrid } from '../../../../../components/grid';
import { type ActionColumn } from '../../../../../components/grid/actions-column';
import GridApplication from '../../../../../components/grid/gridApplication';
import { WarningDialog } from '../../../../../core/dialog';
import { useOperationsList } from '../../../../Operations/EconomyActivity/hooks/useOperationsList';
import {
  type LedgerOperationSummary,
  type OperationReportRequest,
  type ReportVector
} from '../../models/types';
import { useGetAllReports } from '../../services/hooks';
import { baseUrl, deleteReportOperation } from '../../services/http';
import { headers, label, messages, title } from '../../translation';

const Index = () => {
  const [search, setSearch] = useState<any>({});
  const [toggleReload, setToggleReload] = useState<any>(true);
  const { t } = useTranslation();
  const [{ data: operations, loading: loadingOperations, error: errroOperations }] =
    useOperationsList();

  const { data: reports, loading: loadingReports, error: errorReports } = useGetAllReports();

  const navigate = useNavigate();

  const onSearch = (values: ReportVector) => {
    setSearch({ operationId: values?.operation?.id, ledgerId: values?.report?.id });
  };

  const remove = async (row: LedgerOperationSummary) => {
    const ok = await WarningDialog({
      titleModal: title('warning'),
      message: messages('removeReportOperation'),
      okButton: buttonCommon(t, 'delete')
    });
    if (ok) {
      try {
        await deleteReportOperation({
          reportId: row.ledgerId,
          operationId: row.operationId
        } as OperationReportRequest);
        notificationSuccess(i18next.t('taxes.messages.deletedSuccessfully'));
        setToggleReload((prev: any) => !prev);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const actions: Array<ActionColumn> = [
    {
      action: ButtonAction.delete,
      variant: 'danger',

      onClick: async (row: LedgerOperationSummary) => {
        await remove(row);
      }
    }
  ];
  const colDef: Array<ColDef> = [
    {
      headerName: headers('actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },
    { headerName: headers('operation'), field: 'operationName' },
    {
      headerName: headers('report'),
      field: 'ledgerName'
    }
  ];

  const newTax = () => {
    navigate(`/taxes/manage/reports/create`);
  };

  if (loadingOperations || errroOperations || loadingReports || errorReports) {
    return <></>;
  }

  return (
    <>
      <PageTitle title={title('page')} />

      <HookFormResolver onSubmit={onSearch}>
        {({ reset }: HookFormMethods) => {
          return (
            <>
              {' '}
              <Row>
                <Col>
                  <Combobox
                    label={label('report')}
                    name="report"
                    display="name"
                    code="id"
                    data={reports}
                  />
                </Col>
                <Col>
                  <Combobox
                    label={label('operation')}
                    name="operation"
                    display="name"
                    code="id"
                    data={operations}
                  />
                </Col>
              </Row>
              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    type={'button'}
                    onClick={() => {
                      reset({ tax: null, operation: null });
                      setSearch({});
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.new} onClick={newTax} />
                  <Button action={ButtonAction.find} type={'submit'} variant={'primary'} />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>

      <hr />
      <Row>
        <Col>
          <GridApplication
            toggleReload={toggleReload}
            url={baseUrl}
            columnDefs={colDef}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
};

export default Index;
